/* eslint-disable @stencil-community/required-jsdoc */
import { Component, Host, h, Prop, Watch, Event, EventEmitter, State } from '@stencil/core';
import Player from '@vimeo/player';
import type { Options, TimeEvent } from 'vimeo__player';

@Component({
  tag: 'efb-video-modal-item',
  styleUrl: 'video-modal-item.scss',
  shadow: true,
})
export class EfbVideoModalItem {
  private videoPlayerRef: HTMLElement;

  private vimeoPlayer: Player;

  private lastPortion: number = 0.75;

  /**
   * State
   */

  @State() isInLastPortion: boolean = false;

  /**
   * Props
   */

  @Prop() trackingId: string;

  @Prop() index?: string;

  @Prop() vimeoId: string;

  @Prop() isActiveItem?: boolean;

  @Watch('isActiveItem')
  async handleVideoLoading(newValue: boolean) {
    // if slide is active item, play video
    if (newValue) {
      this.vimeoPlayer.play();

      // give the current video length to video-modal parent
      const videoDuration = await this.vimeoPlayer.getDuration();

      this.setCurrentVideoDuration.emit(videoDuration);
    } else {
      this.vimeoPlayer.pause();
      this.vimeoPlayer.setCurrentTime(0);
    }
  }

  @Prop() videoIsMuted?: boolean;

  @Watch('videoIsMuted')
  async videoIsMutedHandler(newValue: boolean) {
    if (this.isActiveItem) {
      this.handleVideoSound(newValue);
    }
  }

  @Prop() playVideo?: boolean;

  @Watch('playVideo')
  async playVideoHandler(newValue: boolean) {
    if (this.isActiveItem) {
      if (newValue) {
        this.vimeoPlayer.play();
      } else {
        this.vimeoPlayer.pause();
      }
    }
  }

  @Prop() seekTo?: number;

  @Watch('seekTo')
  async seekToHandler(newValue: number) {
    if (this.isActiveItem) {
      this.vimeoPlayer.setCurrentTime(newValue);
    }
  }

  /**
   * Event
   */

  @Event() videoEnded: EventEmitter;

  @Event() videoIsReady: EventEmitter;

  @Event() videoIsPlaying: EventEmitter;

  @Event() videoTimeUpdated: EventEmitter;

  @Event() setCurrentVideoDuration: EventEmitter;

  @Event() hasEnteredLastPortion: EventEmitter;

  /**
   * Methods
   */

  private handleVideoSound(needsToBeMuted: boolean) {
    const volume = needsToBeMuted ? 0 : 1;

    this.vimeoPlayer.setVolume(volume);
  }

  /**
   * Lifecycle Hooks
   */

  async componentDidLoad() {
    const videoOptions: Options = {
      // eslint-disable-next-line radix
      // url: `https://player.vimeo.com/video/${parseInt(this.vimeoId)}?h=8ddfb71098`,
      id: +this.vimeoId,
      autoplay: false,
      controls: false,
      portrait: false,
      title: false,
      byline: false,
      color: 'e9c0e9',
    };

    this.vimeoPlayer = new Player(this.videoPlayerRef, videoOptions);

    // for video modal with only one video
    if (this.isActiveItem) {
      this.handleVideoLoading(this.isActiveItem);
    }

    // Vimeo player's event listeners
    this.vimeoPlayer.on('loaded', () => {
      this.videoIsReady.emit();
    });

    this.vimeoPlayer.on('play', () => {
      const videoNeedsToBeMuted = sessionStorage.getItem('muteVideos') === 'true';

      this.handleVideoSound(videoNeedsToBeMuted);

      this.videoIsPlaying.emit(true);
    });

    this.vimeoPlayer.on('pause', () => {
      if (this.isActiveItem) {
        this.videoIsPlaying.emit(false);
      }
    });

    this.vimeoPlayer.on('ended', () => {
      this.videoIsPlaying.emit(false);
      this.videoEnded.emit();
      this.vimeoPlayer.setCurrentTime(0);
    });

    this.vimeoPlayer.on('timeupdate', (e: TimeEvent) => {
      this.videoTimeUpdated.emit(e.percent);

      if (!this.isInLastPortion && e.percent >= this.lastPortion) {
        this.hasEnteredLastPortion.emit();
        this.isInLastPortion = true;
      }
    });
  }

  /**
   * Render
   */

  render() {
    return (
      <Host>
        <div class="video-modal-item">
          {/* Video Player */}
          <div
            class="video-player"
            ref={(el) => {
              this.videoPlayerRef = el;
            }}
          ></div>
        </div>
      </Host>
    );
  }
}
